import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material'
import {
  ErrorOutline as ErrorOutlineIcon,
  MailOutline as MailOutlineIcon,
  Person as PersonIcon,
} from '@mui/icons-material'
import { userApi, invitationApi, User } from '../../services/userApi'
import { isAllowedEmailDomain, getAllDomainsDisplay } from '../../utils/emailValidation'
import { poolwerxColors } from '../../theme/colors'
import BaseDialog from '../../components/shared/dialogs/BaseDialog'
import DialogFooter from '../../components/shared/dialogs/DialogFooter'
import ConfirmDialog from '../../components/shared/dialogs/ConfirmDialog'
import ConfirmUserFpBody from '../../components/admin/shared/ConfirmUserFpBody'
import FormErrorSummary from '../../components/admin/shared/FormErrorSummary'
import { AdminFormSection, FieldLabel } from '../../components/admin/shared/fields'

interface AddFPUserDialogProps {
  open: boolean
  fpCode: string
  fpName?: string
  existingUserIds: Set<number>
  existingInvitedEmails: Set<string>
  onClose: () => void
  onGranted: (message: string) => void
  onInvited: (message: string) => void
}

type DropdownOption =
  | { kind: 'user'; id: string; user: User }
  | { kind: 'info'; id: string; message: string }
  | {
      kind: 'invite'
      id: string
      email: string
      disabled: boolean
      invalid: boolean
      reason: string
    }

type PendingAction =
  | { kind: 'grant'; user: User }
  | { kind: 'invite'; email: string }

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const MIN_SEARCH_LENGTH = 3
const DEBOUNCE_MS = 300

const AddFPUserDialog: React.FC<AddFPUserDialogProps> = ({
  open,
  fpCode,
  fpName,
  existingUserIds,
  existingInvitedEmails,
  onClose,
  onGranted,
  onInvited,
}) => {
  const [inputValue, setInputValue] = useState('')
  const [debouncedInput, setDebouncedInput] = useState('')
  const [results, setResults] = useState<User[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [conflictingRole, setConflictingRole] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null)

  const searchAbortRef = useRef<AbortController | null>(null)
  const conflictAbortRef = useRef<AbortController | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const trimmed = inputValue.trim()
  const hasAnyInput = trimmed.length > 0
  const hasEnoughChars = trimmed.length >= MIN_SEARCH_LENGTH
  const isCompleteEmail = EMAIL_REGEX.test(trimmed)
  const domainAllowed = isCompleteEmail ? isAllowedEmailDomain(trimmed) : false
  const alreadyInvited = isCompleteEmail
    ? existingInvitedEmails.has(trimmed.toLowerCase())
    : false

  useEffect(() => {
    if (open) {
      setInputValue('')
      setDebouncedInput('')
      setResults([])
      setError(null)
      setConflictingRole(null)
      setSubmitting(false)
      setDropdownOpen(false)
      setPendingAction(null)
    } else {
      searchAbortRef.current?.abort()
      conflictAbortRef.current?.abort()
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [open])

  const runSearch = useCallback((query: string) => {
    if (query.length < MIN_SEARCH_LENGTH) {
      setResults([])
      setSearchLoading(false)
      return
    }

    searchAbortRef.current?.abort()
    const controller = new AbortController()
    searchAbortRef.current = controller
    setSearchLoading(true)

    userApi
      .listUsers('FP_USER', undefined, query)
      .then((res) => {
        if (controller.signal.aborted) return
        setResults(res.data || [])
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        console.error('FP user search failed:', err)
        setResults([])
      })
      .finally(() => {
        if (!controller.signal.aborted) setSearchLoading(false)
      })
  }, [])

  // Detect non-FP_USER accounts (exact-match lookup) so we can colour-block
  // the invite row.
  useEffect(() => {
    if (!isCompleteEmail) {
      setConflictingRole(null)
      return
    }

    conflictAbortRef.current?.abort()
    const controller = new AbortController()
    conflictAbortRef.current = controller

    userApi
      .listUsers(undefined, undefined, trimmed)
      .then((res) => {
        if (controller.signal.aborted) return
        const match = (res.data || []).find(
          (u) => u.email.toLowerCase() === trimmed.toLowerCase(),
        )
        if (match && match.role !== 'FP_USER') {
          setConflictingRole(match.role)
        } else {
          setConflictingRole(null)
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setConflictingRole(null)
      })

    return () => controller.abort()
  }, [isCompleteEmail, trimmed])

  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, newInputValue: string, reason: string) => {
      setInputValue(newInputValue)
      if (reason !== 'input') return

      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        const q = newInputValue.trim()
        setDebouncedInput(q)
        runSearch(q)
      }, DEBOUNCE_MS)
    },
    [runSearch],
  )

  const visibleResults = useMemo(
    () => results.filter((u) => !existingUserIds.has(u.id)),
    [results, existingUserIds],
  )

  const options: DropdownOption[] = useMemo(() => {
    const opts: DropdownOption[] = []

    // Existing-user rows.
    if (hasEnoughChars && !searchLoading) {
      if (visibleResults.length > 0) {
        visibleResults.forEach((u) => {
          opts.push({ kind: 'user', id: `user-${u.id}`, user: u })
        })
      } else if (debouncedInput.length >= MIN_SEARCH_LENGTH) {
        opts.push({
          kind: 'info',
          id: 'no-results',
          message: `No Franchise Partners found matching "${trimmed}"`,
        })
      }
    } else if (hasAnyInput && !hasEnoughChars) {
      opts.push({
        kind: 'info',
        id: 'too-short',
        message: `Type at least ${MIN_SEARCH_LENGTH} characters to search`,
      })
    }

    // Invite row — always present, but reflects validity.
    let invalid = false
    let disabled = false
    let reason = 'Generates a tokenised sign-up link to share with them manually.'

    if (!hasAnyInput) {
      invalid = true
      disabled = true
      reason = 'Enter an email address above'
    } else if (!isCompleteEmail) {
      invalid = true
      disabled = true
      reason = 'Enter a valid email address'
    } else if (!domainAllowed) {
      invalid = true
      disabled = true
      reason = `Email domain not allowed. Use one of: ${getAllDomainsDisplay()}`
    } else if (conflictingRole) {
      invalid = true
      disabled = true
      reason = `This email belongs to a ${conflictingRole} account - FP invitations can only be sent to Franchise Partners.`
    } else if (alreadyInvited) {
      disabled = true
      reason = 'An active invitation already exists for this email.'
    }

    opts.push({
      kind: 'invite',
      id: 'invite-row',
      email: trimmed,
      disabled,
      invalid,
      reason,
    })

    return opts
  }, [
    hasEnoughChars,
    searchLoading,
    visibleResults,
    debouncedInput,
    trimmed,
    hasAnyInput,
    isCompleteEmail,
    domainAllowed,
    conflictingRole,
    alreadyInvited,
  ])

  const handleSelect = useCallback(
    (_event: React.SyntheticEvent, value: DropdownOption | string | null) => {
      if (!value || typeof value === 'string') return
      if (value.kind === 'info') return
      if (value.kind === 'invite' && value.disabled) return

      if (value.kind === 'user') {
        setPendingAction({ kind: 'grant', user: value.user })
      } else {
        setPendingAction({ kind: 'invite', email: value.email })
      }
      setDropdownOpen(false)
    },
    [],
  )

  const handleConfirm = useCallback(async () => {
    if (!pendingAction) return
    setError(null)
    setSubmitting(true)
    try {
      if (pendingAction.kind === 'grant') {
        await userApi.grantFPAccess(pendingAction.user.id, fpCode)
        onGranted(
          `${pendingAction.user.name || pendingAction.user.email} granted access`,
        )
      } else {
        await invitationApi.createInvitation({
          fp_code: fpCode,
          email: pendingAction.email,
        })
        onInvited(`Invitation created for ${pendingAction.email}`)
      }
      setPendingAction(null)
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Action failed')
      setPendingAction(null)
    } finally {
      setSubmitting(false)
    }
  }, [pendingAction, fpCode, onGranted, onInvited])

  return (
    <>
      <BaseDialog
        open={open}
        onClose={onClose}
        title="Add FP User"
        subtitle={fpName || fpCode}
        variant="edit"
        size="sm"
        footer={<DialogFooter cancel={{ onClick: onClose, disabled: submitting }} />}
      >
          <FormErrorSummary message={error} />

          <Grid container spacing={2}>
            <AdminFormSection
              card
              title="Find or invite a user"
              caption="Grant access to an existing user, or invite a new one by email."
            >
              <FieldLabel>Email</FieldLabel>
              <Autocomplete<DropdownOption, false, false, true>
            freeSolo
            autoHighlight
            open={dropdownOpen}
            onOpen={() => setDropdownOpen(true)}
            onClose={() => setDropdownOpen(false)}
            options={options}
            loading={searchLoading}
            disabled={submitting}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onChange={handleSelect}
            filterOptions={(x) => x}
            clearOnBlur={false}
            blurOnSelect
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option
              if (option.kind === 'user') return option.user.email
              if (option.kind === 'invite') return option.email
              return ''
            }}
            getOptionDisabled={(option) => {
              if (option.kind === 'info') return true
              if (option.kind === 'invite') return option.disabled
              return !option.user.is_active
            }}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            loadingText="Searching..."
            renderOption={(props, option) => {
              if (option.kind === 'info') {
                return (
                  <Box
                    component="li"
                    {...props}
                    key={option.id}
                    sx={{
                      py: 1.5,
                      pointerEvents: 'none',
                      color: 'text.secondary',
                      fontStyle: 'italic',
                    }}
                  >
                    <Typography variant="body2">{option.message}</Typography>
                  </Box>
                )
              }

              if (option.kind === 'user') {
                const u = option.user
                const fpCount = u.franchise_access?.length ?? 0
                const inactive = !u.is_active
                return (
                  <Box component="li" {...props} key={option.id} sx={{ py: 1 }}>
                    <PersonIcon
                      fontSize="small"
                      sx={{ mr: 1, color: poolwerxColors.teal }}
                    />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                        {u.name}
                        {inactive && (
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ ml: 1, color: 'warning.main' }}
                          >
                            (inactive)
                          </Typography>
                        )}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', display: 'block' }}
                        noWrap
                      >
                        {u.email}
                      </Typography>
                    </Box>
                    <Chip
                      label={`${fpCount} FP${fpCount === 1 ? '' : 's'}`}
                      size="small"
                      variant="outlined"
                      sx={{ ml: 1 }}
                    />
                  </Box>
                )
              }

              // invite row
              const iconColor = option.invalid ? 'error.main' : poolwerxColors.teal
              const primaryColor = option.invalid ? 'error.main' : 'text.primary'
              return (
                <Box
                  component="li"
                  {...props}
                  key={option.id}
                  sx={{
                    py: 1,
                    borderTop: 1,
                    borderColor: 'divider',
                    bgcolor: 'action.hover',
                  }}
                >
                  {option.invalid ? (
                    <ErrorOutlineIcon fontSize="small" sx={{ mr: 1, color: iconColor }} />
                  ) : (
                    <MailOutlineIcon fontSize="small" sx={{ mr: 1, color: iconColor }} />
                  )}
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: primaryColor }}
                      noWrap
                    >
                      {option.email
                        ? `Create invitation for "${option.email}"`
                        : 'Create invitation'}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: option.invalid ? 'error.main' : 'text.secondary',
                        display: 'block',
                      }}
                      noWrap
                    >
                      {option.reason}
                    </Typography>
                  </Box>
                </Box>
              )
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                size="small"
                placeholder="user@poolwerx.com.au"
                error={isCompleteEmail && !domainAllowed}
                helperText={
                  isCompleteEmail && !domainAllowed
                    ? `Email must be one of: ${getAllDomainsDisplay()}`
                    : ' '
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searchLoading ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{
              '& .MuiAutocomplete-listbox': {
                maxHeight: 320,
              },
            }}
          />
            </AdminFormSection>
          </Grid>
      </BaseDialog>

      <ConfirmActionDialog
        action={pendingAction}
        submitting={submitting}
        fpCode={fpCode}
        fpName={fpName}
        onCancel={() => setPendingAction(null)}
        onConfirm={handleConfirm}
      />
    </>
  )
}

interface ConfirmActionDialogProps {
  action: PendingAction | null
  submitting: boolean
  fpCode: string
  fpName?: string
  onCancel: () => void
  onConfirm: () => void
}

const ConfirmActionDialog: React.FC<ConfirmActionDialogProps> = ({
  action,
  submitting,
  fpCode,
  fpName,
  onCancel,
  onConfirm,
}) => {
  const open = action !== null
  const fpDisplayName = fpName || fpCode
  const fps = [{ name: fpDisplayName, fpCode }]

  const title = action?.kind === 'invite' ? 'Create invitation' : 'Grant access'
  const body =
    action?.kind === 'grant' ? (
      <ConfirmUserFpBody
        subjectKind="user"
        subjectLabel="User"
        subjectPrimary={action.user.name || action.user.email}
        subjectSecondary={action.user.email}
        fpLabel="Franchise Partner"
        fps={fps}
        notice={{
          severity: 'info',
          content: 'The user will receive FP_USER access for this Franchise Partner.',
        }}
      />
    ) : action?.kind === 'invite' ? (
      <ConfirmUserFpBody
        subjectKind="invite"
        subjectLabel="Invite"
        subjectPrimary={action.email}
        fpLabel="Franchise Partner"
        fps={fps}
        notice={{
          severity: 'info',
          content:
            'Generates a tokenised FP_USER sign-up link for this Franchise Partner to copy and share manually.',
        }}
      />
    ) : null
  const confirmLabel = action?.kind === 'grant' ? 'Grant access' : 'Create Invite'

  return (
    <ConfirmDialog
      open={open}
      onClose={onCancel}
      onConfirm={onConfirm}
      title={title}
      subtitle={fpDisplayName}
      body={body}
      tone="neutral"
      size="md"
      confirmLabel={confirmLabel}
      pending={submitting}
    />
  )
}

export default AddFPUserDialog
